// Graph traversal primitives (ARCHITECTURE.md §3, src/graph).
//
// BFS, DFS and transitive closure over an AdjacencyListGraph following directed
// out-edges (from → to). All three are visited-set guarded, so cycles and
// parallel edges terminate. bfs/dfs are generators yielding GraphNode in visit
// order; transitiveClosure returns the reachable id set (excluding the start).
//
// A missing start id is treated as an empty traversal rather than an error:
// downstream callers routinely probe ids that may have been pruned.

import type { AdjacencyListGraph } from "./adjacency.js";
import type { GraphNode } from "../types.js";

/**
 * Yield nodes reachable from `startNodeId` in breadth-first order, starting
 * with the start node itself. Each node is yielded at most once.
 */
export function* bfs(
  graph: AdjacencyListGraph,
  startNodeId: string,
): Generator<GraphNode> {
  if (!graph.hasNode(startNodeId)) {
    return;
  }

  const visited = new Set<string>([startNodeId]);
  const queue: string[] = [startNodeId];

  while (queue.length > 0) {
    const id = queue.shift() as string;
    const node = graph.getNode(id);
    if (node) {
      yield node;
    }
    for (const neighbor of graph.getNeighbors(id)) {
      if (!visited.has(neighbor)) {
        visited.add(neighbor);
        queue.push(neighbor);
      }
    }
  }
}

/**
 * Yield nodes reachable from `startNodeId` in depth-first (pre-order) order,
 * starting with the start node itself. Neighbours are explored in edge
 * insertion order. Each node is yielded at most once.
 */
export function* dfs(
  graph: AdjacencyListGraph,
  startNodeId: string,
): Generator<GraphNode> {
  if (!graph.hasNode(startNodeId)) {
    return;
  }

  const visited = new Set<string>();
  // Explicit stack to keep traversal iterative. Push neighbours in reverse so
  // they pop in insertion order, matching a recursive pre-order DFS.
  const stack: string[] = [startNodeId];

  while (stack.length > 0) {
    const id = stack.pop() as string;
    if (visited.has(id)) {
      continue;
    }
    visited.add(id);

    const node = graph.getNode(id);
    if (node) {
      yield node;
    }

    const neighbors = graph.getNeighbors(id);
    for (let i = neighbors.length - 1; i >= 0; i -= 1) {
      if (!visited.has(neighbors[i])) {
        stack.push(neighbors[i]);
      }
    }
  }
}

/**
 * Return the set of node ids reachable from `startNodeId` via directed edges,
 * excluding the start node itself. An unknown start id yields an empty set.
 */
export function transitiveClosure(
  graph: AdjacencyListGraph,
  startNodeId: string,
): Set<string> {
  const reachable = new Set<string>();
  if (!graph.hasNode(startNodeId)) {
    return reachable;
  }

  const stack: string[] = [...graph.getNeighbors(startNodeId)];
  while (stack.length > 0) {
    const id = stack.pop() as string;
    if (reachable.has(id)) {
      continue;
    }
    reachable.add(id);
    for (const neighbor of graph.getNeighbors(id)) {
      if (!reachable.has(neighbor)) {
        stack.push(neighbor);
      }
    }
  }

  // The start node is only "reachable from itself" when a cycle leads back to
  // it; in that case it is added naturally above. Otherwise it is excluded.
  return reachable;
}
